import { APIPath } from '../constant'
import { BaseService } from './base'

class Subscription {
    getUsage() {
        return BaseService.get(APIPath.subscriptionUsage)
    }

    getPaymentStatus() {
        return BaseService.get(APIPath.paymentStatus)
    }

    /** Stripe publishable key for this web client. Source defaults to website. */
    getPublicKey({ clientId, source = 'website' }) {
        const params = new URLSearchParams()
        params.set('ClientId', String(clientId))
        if (source) params.set('Source', source)
        return BaseService.get(`${APIPath.publicKey}?${params.toString()}`)
    }

    getPaymentFormat() {
        return BaseService.get(APIPath.paymentFormat)
    }

    /** Plan catalogue — QA requires X-TOKEN (mockup said public; BE enforces auth). */
    getServicePlans() {
        return BaseService.get(APIPath.servicePlan)
    }

    /** Body is empty. Only PlanId and IsAutoRenewal are accepted. */
    createPurchaseOrder({ planId, isAutoRenewal = true }) {
        const qs = `?PlanId=${encodeURIComponent(planId)}&IsAutoRenewal=${
            isAutoRenewal ? 'true' : 'false'
        }`
        return BaseService.postNoBody(`${APIPath.purchaseOrder}${qs}`)
    }

    confirmPayment(payload) {
        return BaseService.postExact(APIPath.paymentConfirm, payload)
    }

    /**
     * Finish an existing payment order. This does not price a new cart
     * and does not credit a previous charge.
     */
    payNow(transaction) {
        const row = transaction && typeof transaction === 'object' ? transaction : {}
        return BaseService.postExact(APIPath.payNow, {
            PaymentOrderId: row.PaymentOrderId || row.paymentOrderId || '',
            ChargeId: row.ChargeId || row.chargeId || '',
            TransactionId: row.TransactionId || row.transactionId || '',
            CreatedOn: row.CreatedOn || row.createdOn || null,
            ResponseMessage: row.ResponseMessage || row.responseMessage || '',
            DisplayMessage: row.DisplayMessage || row.displayMessage || '',
            ChargeAmount: row.ChargeAmount ?? row.chargeAmount ?? 0,
            status: row.status || row.Status || '',
            OrderNo: row.OrderNo ?? row.orderNo ?? 0,
            ClientSecretId: row.ClientSecretId || row.clientSecretId || '',
            MerchantDisplayName:
                row.MerchantDisplayName || row.merchantDisplayName || '',
            PaymentMethodId: row.PaymentMethodId || row.paymentMethodId || '',
            ThirdPartyClientId:
                row.ThirdPartyClientId || row.thirdPartyClientId || '',
            EphemeralKey: row.EphemeralKey || row.ephemeralKey || '',
            EphemeralKeySecret:
                row.EphemeralKeySecret || row.ephemeralKeySecret || '',
            CompanyId: row.CompanyId ?? row.companyId ?? 0,
        })
    }

    cartRequest({ seats, billingFrequency, expectedTotal }) {
        return {
            Seats: Number(seats),
            BillingFrequency: String(billingFrequency),
            ExpectedTotal: Math.round(Number(expectedTotal) * 100) / 100,
        }
    }

    /** ExpectedTotal is the price check. The server still calculates the charge. */
    quoteCart(request) {
        return BaseService.postExact(APIPath.cartQuote, this.cartRequest(request))
    }

    checkoutCart(request) {
        return BaseService.postExact(
            APIPath.cartCheckout,
            this.cartRequest(request),
        )
    }

    confirmCart({ cartOrderId, paymentOrderId }) {
        return BaseService.postExact(APIPath.cartConfirm, {
            CartOrderId: Number(cartOrderId),
            PaymentOrderId: String(paymentOrderId),
        })
    }

    getCartPricing() {
        return BaseService.get(APIPath.cartPricing)
    }

    getCartOrder(cartOrderId) {
        return BaseService.get(APIPath.cartOrder(cartOrderId))
    }

    cancelPlan() {
        return BaseService.postNoBody(APIPath.cancelPlan)
    }

    /** Query only. Body is empty. */
    acceptOffer(offerId) {
        return BaseService.postNoBody(
            `${APIPath.offerAccepted}?OfferId=${encodeURIComponent(offerId)}`,
        )
    }

    getRetentionOffer() {
        return BaseService.get(APIPath.retentionOffer)
    }

    getPaymentHistory({ pageNumber = 1, pageSize = 1 } = {}) {
        return BaseService.get(
            `${APIPath.paymentHistory}?PageNumber=${pageNumber}&PageSize=${pageSize}`,
        )
    }
}

const SubscriptionService = new Subscription()
Object.freeze(SubscriptionService)
export { SubscriptionService }
